import { cn } from '@/lib/cn';
import { BUCKET_TO_TAILWIND, type Bucket } from '@/lib/status-buckets';

// Plan 08-03 — horizontal stacked bar primitive used by the campaign
// progress panel. Each segment renders `width: %` proportional to count
// against `total`. Zero-count segments collapse but stay in the legend so
// operators can see absent buckets at a glance.

export interface StatusBarSegment {
  key: string;
  label: string;
  count: number;
  bucket: Bucket;
}

export interface StatusBarProps {
  segments: readonly StatusBarSegment[];
  total: number;
  className?: string;
  ariaLabel?: string;
}

export const StatusBar = ({ segments, total, className, ariaLabel }: StatusBarProps) => {
  const safeTotal = Math.max(0, total);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={safeTotal}
        className="flex h-3 w-full overflow-hidden rounded bg-muted"
      >
        {safeTotal === 0
          ? null
          : segments.map((seg) => {
              const pct = (seg.count / safeTotal) * 100;
              if (pct <= 0) return null;
              return (
                <div
                  key={seg.key}
                  className={cn('h-full', BUCKET_TO_TAILWIND[seg.bucket])}
                  style={{ width: `${String(pct)}%` }}
                  title={`${seg.label}: ${String(seg.count)}`}
                  data-testid={`status-bar-segment-${seg.key}`}
                />
              );
            })}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {segments.map((seg) => (
          <li key={seg.key} className="inline-flex items-center gap-1.5">
            <span
              className={cn('inline-block h-2 w-2 rounded-full', BUCKET_TO_TAILWIND[seg.bucket])}
              aria-hidden="true"
            />
            <span>
              {seg.label}: <span className="text-foreground">{seg.count}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
